// Art-bible palette. Stylized industrial sci-fi: crisp silhouettes, restrained
// surface detail, controlled neon accents. Bright saturated colours are reserved
// for threats, loot, powers and important interface elements; environments
// stay dark and desaturated so gameplay reads first.

export const COLORS = {
  // Neutral materials
  ink: 0x0b0e14, // outlines, deepest shadow
  steel0: 0x161b23,
  steel1: 0x1f2630,
  steel2: 0x2c3542,
  steel3: 0x48566a,
  steel4: 0x7d8ea6,
  steel5: 0xb9c6d6, // edge highlight
  gunmetal: 0x3a3532,
  rust: 0x6b4a36,

  // Mech reactors (player identity)
  volt: 0x35e8ff,
  voltGlow: 0x9ff6ff,
  bastion: 0xffb02e,
  bastionGlow: 0xffd98a,
  wraith: 0xb266ff,
  wraithGlow: 0xe0c2ff,

  // Threats (enemies, their projectiles and telegraphs)
  threat: 0xff3b5c,
  threatHot: 0xff7a1a,
  threatGlow: 0xff9db0,
  telegraph: 0xffe14d, // warning fill; always paired with a shape + animation

  // Loot
  orbArsenal: 0xffd23f,
  orbArmor: 0x4dff9a,
  orbUnstable: 0xff3df2,
  scrap: 0xd9b26a,
  heal: 0x5dffa8,

  // Rarity
  standard: 0xc9d3df,
  advanced: 0x4fb3ff,
  prototype: 0xff4fd8,

  // Tags / damage types
  electric: 0x7df9ff,
  burn: 0xff8c2a,
  explosive: 0xffcf4a,
  shield: 0x6ea8ff,
  gravity: 0x9b6bff,
  dash: 0xe8f4ff,
  kinetic: 0xdfe6ee,
  precision: 0xff5a5a,
  heat: 0xff6a3d,
  missile: 0xffa94d,
  chain: 0x9ff6ff,

  // Interface
  uiBg: 0x0a0e14,
  uiPanel: 0x111823,
  uiBorder: 0x2f3b4d,
  uiText: 0xe6edf5,
  uiDim: 0x8a97a8,
  uiGood: 0x5dffa8,
  uiBad: 0xff5d73,
  hp: 0xff4d6d,
  shieldBar: 0x6ea8ff,
  heatBar: 0xff8c2a,
  ultBar: 0xfff27a,
} as const;

export type ColorName = keyof typeof COLORS;

/** 0xRRGGBB → '#rrggbb' for Canvas2D / Phaser text styles. */
export function css(color: number, alpha = 1): string {
  const r = (color >> 16) & 0xff;
  const g = (color >> 8) & 0xff;
  const b = color & 0xff;
  if (alpha >= 1) return `#${color.toString(16).padStart(6, '0')}`;
  return `rgba(${r},${g},${b},${alpha})`;
}

/** Linear blend of two 0xRRGGBB colours, t in [0,1]. */
export function mix(a: number, b: number, t: number): number {
  const ar = (a >> 16) & 0xff, ag = (a >> 8) & 0xff, ab = a & 0xff;
  const br = (b >> 16) & 0xff, bg = (b >> 8) & 0xff, bb = b & 0xff;
  const r = Math.round(ar + (br - ar) * t);
  const g = Math.round(ag + (bg - ag) * t);
  const bl = Math.round(ab + (bb - ab) * t);
  return (r << 16) | (g << 8) | bl;
}

/** Multiply brightness (t < 1 darkens, t > 1 lightens, clamped). */
export function shade(color: number, t: number): number {
  const r = Math.min(255, Math.round(((color >> 16) & 0xff) * t));
  const g = Math.min(255, Math.round(((color >> 8) & 0xff) * t));
  const b = Math.min(255, Math.round((color & 0xff) * t));
  return (r << 16) | (g << 8) | b;
}

/** Colour for a gameplay tag. */
export function tagColor(tag: string): number {
  return (COLORS as Record<string, number>)[tag] ?? COLORS.uiText;
}

export function rarityColor(rarity: 'standard' | 'advanced' | 'prototype'): number {
  return COLORS[rarity];
}
